/**
 * Özel alan motoru sözleşmeleri (WP 2.3, ADR-0004).
 * 36 alan tipi: 31 tiplik ilk Castinflow ürün kataloğu + Teamgram alan tipi keşfinde (2026-09-18,
 * `docs/spec/castinflow/kesif/2026-09-18-alan-tipleri/`) davranışı doğrulanan iki ekleyici tip (`digits`, `heading`) + `files`,
 * `principal_multi` ve yalnız form alan setinde kullanılan `signature` (M6-H2).
 * Teamgram eşlemesi ve açık farklar: aynı dizinde `castinflow-esleme.md`, `farklar-ve-kararlar.md`.
 * Depolama: varlık tablosunda `custom_values jsonb` — anahtar = alan `key`, değer = tipin JSON şekli (aşağıda).
 */
import { z } from 'zod';
import { latitudeSchema, longitudeSchema } from './geo.js';
import { REQUIRED_FIELD_MESSAGE } from './zod-tr.js';
import { RECORD_TYPES, principalSchema } from './org.js';
import { formulaFields, parseFormula, runFormula, } from './formula.js';
export const CUSTOM_FIELD_TYPES = [
    'text', // tek satır metin
    'textarea', // çok satır metin
    'rich_text', // biçimli metin; HTML temizleme HENÜZ uygulanmıyor — düz metin olarak gösterilmeli (WP 2.8 kabul kriteri)
    'email',
    'phone',
    'url',
    'integer',
    'decimal',
    'currency', // {amount, currency}
    'percent',
    'date', // YYYY-MM-DD
    'datetime', // ISO 8601
    'time', // HH:mm
    'month', // YYYY-MM
    'checkbox', // boolean
    'select', // seçeneklerden tek
    'multiselect', // seçeneklerden çok
    'rating', // 1..max
    'user', // kullanıcı uuid
    'department', // departman uuid
    'record', // başka kayıt uuid (options.recordType)
    'record_multi', // kayıt uuid[]
    'file', // {fileId}
    'image', // {fileId}
    'address', // {line1, line2, city, district, postalCode, country}
    'location', // {lat, lng}
    'color', // #rrggbb
    'formula', // hesaplanan (options.expression, options.resultType)
    'iban',
    'identity_no', // TCKN (11) veya VKN (10)
    'country', // ISO 3166-1 alpha-2
    'digits', // yalnız rakamlardan oluşan metin; baştaki sıfırlar korunur (Teamgram "Rakamlar")
    'heading', // form bölüm başlığı; değer taşımaz (Teamgram "Başlık")
    'files', // [{fileId}] çoklu dosya (WP 2.5, AK-5); tekil file/image geriye uyumlu
    'principal_multi', // [{type:'user'|'dept', id}] çoklu kullanıcı/departman (WP 2.8, AK-4); ACL kaynağı DEĞİLDİR
    // "Yetkili imzası" (M6-H2): imza görseli ve onay izi. Değer TAŞIMAZ (değerler sözlüğünde anahtarı yoktur); imza belge
    // revizyonuna bağlı ayrı kayıttır (`form-signatures.ts`). YALNIZ form alan setinde tanımlanır.
    'signature',
];
/**
 * Yalnız form alan setinde (form tanımı 2. adım) kullanılabilen tipler. Çalışma alanı düzeyi özel alan olarak TANIMLANAMAZ:
 * servis 422 döner, `custom_field_defs` tip kısıtı da bu tipleri içermez (ikinci hat).
 */
export const FORM_ONLY_FIELD_TYPES = ['signature'];
export const isFormOnlyFieldType = (t) => FORM_ONLY_FIELD_TYPES.includes(t);
/** Değerler sözlüğünde değeri olmayan tipler (bölüm başlığı, imza alanı): yazılamaz, zorunlu olamaz, liste kolonu olmaz */
export function isValuelessFieldType(t) {
    return t === 'heading' || t === 'signature';
}
export const CUSTOM_FIELD_KEY_RE = /^[a-z][a-z0-9_]{1,39}$/;
/** AK-3 üst sınırı. */
export const MAX_ALLOWED_RECORD_IDS = 200;
/** AK-4 üst sınırı (alan başına principal). */
export const MAX_PRINCIPAL_VALUES = 50;
/** Kayıt başvurusu alanının izinli hedef tipleri (AK-2: `recordTypes` > tekil `recordType`). */
export function targetRecordTypes(def) {
    const many = def.options.recordTypes;
    if (many !== undefined && many.length > 0)
        return [...many];
    return def.options.recordType === undefined ? [] : [def.options.recordType];
}
export const CUSTOM_FIELD_SQL_KIND = {
    text: 'text',
    textarea: null,
    rich_text: null,
    email: 'text',
    phone: 'text',
    url: 'text',
    integer: 'numeric',
    decimal: 'numeric',
    currency: 'numeric',
    percent: 'numeric',
    date: 'date',
    datetime: 'timestamp',
    time: 'text',
    month: 'text',
    checkbox: 'boolean',
    select: 'text',
    multiselect: null,
    rating: 'numeric',
    user: 'text',
    department: 'text',
    record: 'text',
    record_multi: null,
    file: null,
    image: null,
    address: null,
    location: null,
    color: 'text',
    formula: 'text', // gerçek tür options.resultType'a göre (formulaSqlKind)
    iban: 'text',
    identity_no: 'text',
    country: 'text',
    digits: 'text',
    heading: null,
    files: null,
    principal_multi: null,
    signature: null,
};
export const conditionOpSchema = z.enum([
    'eq',
    'ne',
    'in',
    'not_in',
    'gt',
    'gte',
    'lt',
    'lte',
    'contains',
    'is_empty',
    'not_empty',
]);
/** Koşullu görünürlük: başka bir alan (özel `key` veya built-in anahtar) belirli koşulu sağlayınca alan görünür. */
export const fieldConditionSchema = z
    .object({
    field: z.string().min(1).max(60),
    op: conditionOpSchema,
    value: z
        .union([
        z.string().max(500),
        z.number(),
        z.boolean(),
        z.array(z.union([z.string().max(500), z.number()])).max(50),
    ])
        .optional(),
})
    .strict();
export const selectOptionSchema = z
    .object({
    value: z.string().min(1).max(80),
    label: z.string().min(1).max(120),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
    archived: z.boolean().default(false),
})
    .strict();
export const customFieldOptionsSchema = z
    .object({
    maxLength: z.number().int().min(1).max(10_000).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    decimals: z.number().int().min(0).max(6).optional(),
    currency: z.string().length(3).optional(),
    choices: z.array(selectOptionSchema).max(200).optional(),
    ratingMax: z.number().int().min(2).max(10).optional(),
    recordType: z.enum(RECORD_TYPES).optional(),
    /** AK-2: çok biçimli başvuru (`record`/`record_multi`); verilirse `recordType` yerine geçer. Hedef tip defterden çözülür. */
    recordTypes: z.array(z.enum(RECORD_TYPES)).min(1).max(RECORD_TYPES.length).optional(),
    /** AK-3: seçilebilir kayıt kümesi (≤ 200). Metadata'ya girmez; aday ucu ve yama sunucuda uygular. */
    allowedRecordIds: z.array(z.guid()).max(200).optional(),
    /** AK-7: `principal_multi` değerine YENİ eklenen principal'lar kayda yazıcı olarak eklenir (paylaşma yetkisiyle). */
    addToWriters: z.boolean().optional(),
    /**
     * AK-7 (WP 2.9): `principal_multi` değerine YENİ eklenen kullanıcılara (departman → etkin üyeleri, üst sınırlı) uygulama
     * içi bildirim. Varsayılan kapalı; idempotent iş; harici kanal yok; bildirim kayıt erişimi vermez.
     */
    notify: z.boolean().optional(),
    expression: z.string().max(2000).optional(),
    resultType: z.enum(['number', 'text', 'boolean', 'date']).optional(),
    placeholder: z.string().max(120).optional(),
    help: z.string().max(400).optional(),
})
    .strict();
export const visibleForSchema = z
    .object({
    relationTypes: z.array(z.string().min(1).max(60)).max(20).default([]),
    sources: z.array(z.string().min(1).max(60)).max(20).default([]),
})
    .strict();
const defCore = {
    key: z.string().regex(CUSTOM_FIELD_KEY_RE, 'anahtar: küçük harf/rakam/alt çizgi, 2–40 karakter'),
    label: z.string().trim().min(1).max(80),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: customFieldOptionsSchema.prefault({}),
    required: z.boolean().default(false),
    readonly: z.boolean().default(false),
    hideInTemplates: z.boolean().default(false),
    hideInWebForms: z.boolean().default(false),
    showWhenEmpty: z.boolean().default(true),
    askOnQualify: z.boolean().default(false),
    condition: fieldConditionSchema.nullable().default(null),
    visibleFor: visibleForSchema.prefault({}),
    /**
     * Alan düzeyi okuma/yazma principal'ları — WP 2.8'den beri SUNUCUDA uygulanır (`fieldAccessFor`; metadata, katalog, liste,
     * detay, yama, aday ucu). Kural: docs/spec/castinflow/field-security.md.
     */
    readPrincipals: z.array(principalSchema).max(50).default([]),
    writePrincipals: z.array(principalSchema).max(50).default([]),
    sortOrder: z.number().int().min(0).max(10_000).default(0),
};
export const customFieldDefSchema = z.object({
    id: z.guid(),
    recordType: z.enum(RECORD_TYPES),
    ...defCore,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createCustomFieldInputSchema = z.object(defCore).strict();
export const updateCustomFieldInputSchema = z
    .object({
    label: defCore.label.optional(),
    options: customFieldOptionsSchema.optional(),
    required: z.boolean().optional(),
    readonly: z.boolean().optional(),
    hideInTemplates: z.boolean().optional(),
    hideInWebForms: z.boolean().optional(),
    showWhenEmpty: z.boolean().optional(),
    askOnQualify: z.boolean().optional(),
    condition: fieldConditionSchema.nullable().optional(),
    visibleFor: visibleForSchema.optional(),
    readPrincipals: z.array(principalSchema).max(50).optional(),
    writePrincipals: z.array(principalSchema).max(50).optional(),
    sortOrder: defCore.sortOrder.optional(),
})
    .strict();
export const CUSTOM_FIELD_INDEX_STATUSES = [
    'pending',
    'creating',
    'ready',
    'failed',
    'dropping',
    'dropped',
];
export const MAX_CUSTOM_FIELD_INDEXES_PER_TENANT = 20;
/**
 * Tenant başına FİZİKSEL özel alan ikincil indeksi sert sınırı (sahip kararı K9 §10, 2026-09-19). 20 sınırı kullanıcının tanımladığı
 * MANTIKSAL indeksler içindir; bir mantıksal indeksin fiziksel bileşenleri (`customFieldPhysicalComponents`) bu sınıra sayılır.
 * Sınır dolduğunda yeni indeks isteği `PLAN_LIMIT` + `reason: 'physical_index_limit'` ile reddedilir (sessiz indekssiz mod yok).
 */
export const MAX_PHYSICAL_CUSTOM_FIELD_INDEXES_PER_TENANT = 40;
/**
 * Tipli filtre projeksiyonu durumu (K7-E, ADR-0004): `ready` iken liste filtresi RLS altında indeksli çalışır;
 * diğer durumlarda derleyici kanonik JSONB yolunu kullanır (doğru ama indekssiz — performans iddiası yoktur).
 */
export const CUSTOM_FIELD_PROJECTION_STATUSES = [
    'none',
    'pending',
    'backfilling',
    'ready',
    'failed',
    'clearing',
];
export const customFieldIndexSchema = z.object({
    id: z.guid(),
    recordType: z.enum(RECORD_TYPES),
    fieldKey: z.string(),
    indexName: z.string(),
    status: z.enum(CUSTOM_FIELD_INDEX_STATUSES),
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    requestedAt: z.string(),
    readyAt: z.string().nullable(),
    projectionStatus: z.enum(CUSTOM_FIELD_PROJECTION_STATUSES),
    projectionReadyAt: z.string().nullable(),
});
export const customValuesPatchSchema = z.record(z.string().regex(CUSTOM_FIELD_KEY_RE), z.unknown());
// ---------------------------------------------------------------------------
// Tanım doğrulama (tipe özgü seçenek kuralları)
// ---------------------------------------------------------------------------
export function validateFieldDefinition(input, existingKeys) {
    const errs = [];
    const o = input.options;
    if (input.type === 'select' || input.type === 'multiselect') {
        if (!o.choices || o.choices.length === 0)
            errs.push('seçenek listesi gerekli');
        else {
            const vals = new Set(o.choices.map((c) => c.value));
            if (vals.size !== o.choices.length)
                errs.push('seçenek değerleri tekil olmalı');
        }
    }
    const isRecordRef = input.type === 'record' || input.type === 'record_multi';
    if (isRecordRef && !o.recordType && (o.recordTypes === undefined || o.recordTypes.length === 0))
        errs.push('recordType veya recordTypes gerekli');
    if (o.recordTypes !== undefined) {
        if (!isRecordRef)
            errs.push('recordTypes yalnız kayıt başvurusu alanında');
        if (new Set(o.recordTypes).size !== o.recordTypes.length)
            errs.push('recordTypes tekil olmalı');
        if (o.recordType !== undefined && !o.recordTypes.includes(o.recordType))
            errs.push('recordType, recordTypes içinde olmalı');
    }
    if (o.allowedRecordIds !== undefined) {
        if (!isRecordRef)
            errs.push('allowedRecordIds yalnız kayıt başvurusu alanında');
        if (o.allowedRecordIds.length > MAX_ALLOWED_RECORD_IDS)
            errs.push(`allowedRecordIds en fazla ${MAX_ALLOWED_RECORD_IDS}`);
        if (new Set(o.allowedRecordIds.map((x) => x.toLowerCase())).size !== o.allowedRecordIds.length)
            errs.push('allowedRecordIds tekil olmalı');
    }
    if (o.addToWriters === true && input.type !== 'principal_multi')
        errs.push('addToWriters yalnız principal_multi alanında');
    if (o.notify === true && input.type !== 'principal_multi')
        errs.push('notify yalnız principal_multi alanında');
    if (input.type === 'formula') {
        if (!o.expression || o.expression.trim() === '')
            errs.push('expression gerekli');
        if (!o.resultType)
            errs.push('resultType gerekli');
        if (input.required)
            errs.push('formül alanı zorunlu olamaz');
        if (o.expression) {
            try {
                const ast = parseFormula(o.expression);
                const refs = formulaFields(ast);
                if (refs.has(input.key))
                    errs.push('formül kendi alanına referans veremez');
                for (const r of refs)
                    if (!existingKeys.has(r))
                        errs.push(`formülde bilinmeyen alan: ${r}`);
            }
            catch (e) {
                errs.push(`formül hatası: ${e.message}`);
            }
        }
    }
    if (input.type === 'heading' && input.required)
        errs.push('başlık alanı zorunlu olamaz');
    if (input.type === 'signature') {
        // İmza bir değer değil, belge revizyonuna bağlı ayrı kayıttır: alan doğrulamasında zorunlu tutulamaz
        if (input.required)
            errs.push('imza alanı zorunlu olamaz');
        if (input.readonly)
            errs.push('imza alanı salt okunur olamaz');
    }
    if (o.min !== undefined && o.max !== undefined && o.min > o.max)
        errs.push('min > max');
    if (input.type === 'currency' && o.currency !== undefined && !/^[A-Z]{3}$/.test(o.currency))
        errs.push('currency ISO 4217 olmalı');
    return errs;
}
// ---------------------------------------------------------------------------
// Değer şemaları (dinamik zod) ve normalizasyon
// ---------------------------------------------------------------------------
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const PHONE_RE = /^\+?[0-9 ()-]{6,24}$/;
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const COUNTRY_RE = /^[A-Z]{2}$/;
const DIGITS_RE = /^\d+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function validDate(s) {
    if (!DATE_RE.test(s))
        return false;
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
function validIban(s) {
    const v = s.replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(v))
        return false;
    const rearranged = v.slice(4) + v.slice(0, 4);
    let rem = 0;
    for (const ch of rearranged) {
        const code = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
        for (const d of code)
            rem = (rem * 10 + Number(d)) % 97;
    }
    return rem === 1;
}
export function validTckn(s) {
    if (!/^[1-9]\d{10}$/.test(s))
        return false;
    const d = s.split('').map(Number);
    const odd = (d[0] ?? 0) + (d[2] ?? 0) + (d[4] ?? 0) + (d[6] ?? 0) + (d[8] ?? 0);
    const even = (d[1] ?? 0) + (d[3] ?? 0) + (d[5] ?? 0) + (d[7] ?? 0);
    const c10 = (odd * 7 - even) % 10;
    const c11 = (odd + even + (d[9] ?? 0)) % 10;
    return (c10 + 10) % 10 === d[9] && c11 === d[10];
}
export function validVkn(s) {
    if (!/^\d{10}$/.test(s))
        return false;
    const d = s.split('').map(Number);
    let sum = 0;
    for (let i = 0; i < 9; i += 1) {
        const tmp = ((d[i] ?? 0) + (9 - i)) % 10;
        const p = (tmp * 2 ** (9 - i)) % 9;
        sum += tmp === 0 ? 0 : p === 0 ? 9 : p;
    }
    return (10 - (sum % 10)) % 10 === d[9];
}
/** Tek alan için değer şeması (null = boş). */
export function valueSchemaFor(def) {
    const o = def.options;
    const num = (int) => {
        let s = int ? z.number().int() : z.number();
        if (o.min !== undefined)
            s = s.min(o.min);
        if (o.max !== undefined)
            s = s.max(o.max);
        return s;
    };
    const choiceValues = (o.choices ?? []).filter((c) => !c.archived).map((c) => c.value);
    switch (def.type) {
        case 'text':
            return z.string().max(o.maxLength ?? 255);
        case 'textarea':
            return z.string().max(o.maxLength ?? 4000);
        case 'rich_text':
            return z.string().max(o.maxLength ?? 20_000);
        case 'email':
            return z.email().max(254);
        case 'phone':
            return z.string().regex(PHONE_RE, 'telefon biçimi');
        case 'url':
            return z
                .url()
                .max(2048)
                .refine((u) => /^https?:\/\//i.test(u), 'http(s) gerekli');
        case 'integer':
            return num(true);
        case 'decimal':
            return num(false);
        case 'currency':
            return z.object({ amount: num(false), currency: z.string().regex(/^[A-Z]{3}$/) }).strict();
        case 'percent':
            return z
                .number()
                .min(o.min ?? 0)
                .max(o.max ?? 100);
        case 'date':
            return z.string().refine(validDate, 'YYYY-MM-DD');
        case 'datetime':
            return z.iso.datetime({ offset: true });
        case 'time':
            return z.string().regex(TIME_RE, 'HH:mm');
        case 'month':
            return z.string().regex(MONTH_RE, 'YYYY-MM');
        case 'checkbox':
            return z.boolean();
        case 'select':
            return z.string().refine((v) => choiceValues.includes(v), 'geçersiz seçenek');
        case 'multiselect':
            return z
                .array(z.string().refine((v) => choiceValues.includes(v), 'geçersiz seçenek'))
                .max(50);
        case 'rating':
            return z
                .number()
                .int()
                .min(1)
                .max(o.ratingMax ?? 5);
        case 'user':
        case 'department':
        case 'record':
            return z.string().regex(UUID_RE, 'uuid');
        case 'record_multi':
            return z.array(z.string().regex(UUID_RE, 'uuid')).max(50);
        case 'principal_multi':
            // Mevcut principal şeması (user | dept); girişte `department` eş anlamlısı `dept`'e normalize edilir.
            // Yinelenenler sırayı koruyarak tekilleştirilir; varlık/tenant/etkinlik denetimi sunucuda (K-10/K-11).
            return z
                .array(z
                .object({
                type: z.enum(['user', 'dept', 'department']),
                id: z.string().regex(UUID_RE, 'uuid'),
            })
                .strict()
                .transform((p) => ({
                type: p.type === 'department' ? 'dept' : p.type,
                id: p.id.toLowerCase(),
            })))
                .max(MAX_PRINCIPAL_VALUES)
                .transform((a) => {
                const seen = new Set();
                return a.filter((p) => {
                    const k = `${p.type}:${p.id}`;
                    if (seen.has(k))
                        return false;
                    seen.add(k);
                    return true;
                });
            });
        case 'file':
        case 'image':
            return z.object({ fileId: z.string().regex(UUID_RE, 'uuid') }).strict();
        case 'files':
            // Şekil sınırı; tenant politikası (adet/toplam boyut) ve dosyanın bu kayda ait, etkin ve temiz olması sunucuda
            // `validateFileFieldValue` ile denetlenir (apps/api/src/services/attachments-service.ts).
            return z
                .array(z.object({ fileId: z.string().regex(UUID_RE, 'uuid') }).strict())
                .max(100)
                .refine((a) => new Set(a.map((x) => x.fileId)).size === a.length, 'Aynı dosya iki kez verilemez');
        case 'address':
            return z
                .object({
                line1: z.string().max(200).default(''),
                line2: z.string().max(200).default(''),
                city: z.string().max(80).default(''),
                district: z.string().max(80).default(''),
                postalCode: z.string().max(20).default(''),
                country: z.string().regex(COUNTRY_RE).or(z.literal('')).default(''),
            })
                .strict();
        case 'location':
            return z.object({ lat: latitudeSchema, lng: longitudeSchema }).strict();
        case 'color':
            return z.string().regex(COLOR_RE, '#rrggbb');
        case 'formula':
            return z.never();
        case 'iban':
            return z.string().refine(validIban, 'IBAN geçersiz');
        case 'identity_no':
            return z.string().refine((s) => validTckn(s) || validVkn(s), 'TCKN/VKN geçersiz');
        case 'country':
            return z.string().regex(COUNTRY_RE, 'ISO 3166-1 alpha-2');
        case 'digits':
            return z
                .string()
                .max(o.maxLength ?? 255)
                .regex(DIGITS_RE, 'yalnız rakam');
        case 'heading':
            return z.never();
        case 'signature':
            // Değer yazılmaz: imza, imzalama eylemiyle ayrı kayıt olarak atılır
            return z.never();
        default:
            return z.never();
    }
}
function asText(v) {
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number' || typeof v === 'boolean')
        return String(v);
    if (v === null || v === undefined)
        return '';
    return JSON.stringify(v);
}
/**
 * WP 2.9: alanın kayda uygulanabilirliği (`visibleFor`): ilişki tipi ve kaynak listeleri boşsa her kayıtta; doluysa kaydın
 * ilişki tiplerinden biri (built-in `relationTypes`) / kaynağı (`sourceKey`) listede olmalı. Güvenlik kuralı DEĞİLDİR
 * (gösterim/zorunluluk kuralıdır; okuma yetkisi `fieldAccessFor`'dadır).
 */
export function visibleForMatches(vf, builtin) {
    const rel = vf?.relationTypes ?? [];
    const src = vf?.sources ?? [];
    if (rel.length > 0) {
        const have = builtin['relationTypes'];
        if (!Array.isArray(have) || !have.some((x) => rel.includes(String(x))))
            return false;
    }
    if (src.length > 0) {
        const s = builtin['sourceKey'];
        if (typeof s !== 'string' || !src.includes(s))
            return false;
    }
    return true;
}
/** Koşullu görünürlük değerlendirmesi (özel alan + built-in değerleri aynı sözlükte). */
export function evaluateCondition(cond, values) {
    if (cond === null)
        return true;
    const v = values[cond.field];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    switch (cond.op) {
        case 'is_empty':
            return empty;
        case 'not_empty':
            return !empty;
        case 'eq':
            return !empty && asText(v) === String(cond.value);
        case 'ne':
            return empty || asText(v) !== String(cond.value);
        case 'in':
            return !empty && Array.isArray(cond.value) && cond.value.map(String).includes(asText(v));
        case 'not_in':
            return empty || !(Array.isArray(cond.value) && cond.value.map(String).includes(asText(v)));
        case 'contains':
            return (!empty &&
                (Array.isArray(v)
                    ? v.map(asText).includes(String(cond.value))
                    : asText(v)
                        .toLocaleLowerCase('tr-TR')
                        .includes(String(cond.value).toLocaleLowerCase('tr-TR'))));
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte': {
            if (empty || (typeof cond.value !== 'number' && typeof cond.value !== 'string'))
                return false;
            const a = typeof v === 'number' ? v : Number(v);
            const b = typeof cond.value === 'number' ? cond.value : Number(cond.value);
            if (Number.isNaN(a) || Number.isNaN(b))
                return asText(v) > String(cond.value);
            return cond.op === 'gt'
                ? a > b
                : cond.op === 'gte'
                    ? a >= b
                    : cond.op === 'lt'
                        ? a < b
                        : a <= b;
        }
        default:
            return false;
    }
}
/**
 * Yama uygulaması: mevcut değerler + gelen yama → tam doğrulanmış küme.
 * - Bilinmeyen anahtar reddedilir; readonly/formül alanına yazma reddedilir.
 * - Zorunlu alan yalnız görünürse (koşul) zorunludur.
 * - Formül alanları mevcut değerlerden yeniden hesaplanır (hata → null).
 */
export function applyCustomValuesPatch(defs, current, patch, ctx = {}) {
    const issues = [];
    const invalid = new Set();
    const byKey = new Map(defs.map((d) => [d.key, d]));
    // Mevcut değerlerin tamamı korunur (silinmiş tanımların anahtarları dâhil; sözleşme §2: anahtar rezerve, değer kalır)
    const values = { ...current };
    for (const [k, raw] of Object.entries(patch)) {
        const def = byKey.get(k);
        if (!def) {
            issues.push({ key: k, message: 'bilinmeyen alan' });
            continue;
        }
        if (def.type === 'formula') {
            issues.push({ key: k, message: 'formül alanına yazılamaz' });
            continue;
        }
        if (def.type === 'heading') {
            issues.push({ key: k, message: 'başlık alanına yazılamaz' });
            continue;
        }
        if (def.type === 'signature') {
            issues.push({
                key: k,
                message: 'imza alanına değer yazılamaz; imza belge sayfasından atılır',
            });
            continue;
        }
        if (def.readonly &&
            Object.hasOwn(current, k) &&
            current[k] !== null &&
            current[k] !== undefined) {
            issues.push({ key: k, message: 'değiştirilemez alan' });
            continue;
        }
        if (raw === null || raw === undefined || raw === '') {
            values[k] = null;
            continue;
        }
        const parsed = valueSchemaFor(def).safeParse(raw);
        if (!parsed.success) {
            invalid.add(k);
            issues.push({ key: k, message: parsed.error.issues[0]?.message ?? 'geçersiz değer' });
            continue;
        }
        values[k] = parsed.data;
    }
    // Koşullar yalnız AKTİF tanımların değerleri + built-in alanlarla değerlendirilir; silinmiş tanımın korunan değeri
    // görünürlük sonucunu etkilemez (K-5: silinmiş tanım değeri hiçbir yanıta dolaylı yoldan da çıkmaz).
    const activeValues = {};
    for (const [k, v] of Object.entries(values))
        if (byKey.has(k))
            activeValues[k] = v;
    const all = { ...(ctx.builtin ?? {}), ...activeValues };
    for (const def of defs) {
        if (def.type === 'formula' || isValuelessFieldType(def.type) || invalid.has(def.key))
            continue;
        if (ctx.requiredScope !== undefined && !ctx.requiredScope.has(def.key))
            continue;
        const visible = evaluateCondition(def.condition, all) && visibleForMatches(def.visibleFor, all);
        const v = values[def.key];
        const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        if (def.required && visible && empty)
            issues.push({ key: def.key, message: REQUIRED_FIELD_MESSAGE });
    }
    for (const def of computeOrder(defs)) {
        if (def.type !== 'formula')
            continue;
        values[def.key] = computeFormula(def, { ...(ctx.builtin ?? {}), ...values }, ctx.now);
    }
    return { ok: issues.length === 0, values, issues };
}
/** Formül alanlarını bağımlılık sırasına göre döndürür (formül → formül bağımlılığı desteklenir; döngü → sona). */
export function computeOrder(defs) {
    const formulas = defs.filter((d) => d.type === 'formula');
    const others = defs.filter((d) => d.type !== 'formula');
    const done = new Set(others.map((d) => d.key));
    const ordered = [...others];
    const pending = [...formulas];
    let guard = 0;
    while (pending.length > 0 && guard < 100) {
        guard += 1;
        const next = pending.findIndex((d) => {
            try {
                return [...formulaFields(parseFormula(d.options.expression ?? ''))].every((r) => done.has(r) || !formulas.some((f) => f.key === r));
            }
            catch {
                return true;
            }
        });
        const idx = next >= 0 ? next : 0;
        const d = pending.splice(idx, 1)[0];
        if (!d)
            break;
        done.add(d.key);
        ordered.push(d);
    }
    return ordered;
}
export function computeFormula(def, all, now) {
    const expr = def.options.expression ?? '';
    const type = def.options.resultType ?? 'text';
    const fields = {};
    for (const [k, v] of Object.entries(all))
        fields[k] = v;
    const ctx = now === undefined ? { fields } : { fields, now };
    const r = runFormula(expr, ctx, type);
    return r.ok ? r.value : null;
}
/** SQL ifade parçası (indeks/filtre/sıralama): `custom_values` kolonu üzerinden tipli cast. */
/**
 * Mantıksal özel alan indeksinin fiziksel bileşenleri: sıralanabilir alan (ifade türü var) → sıralama indeksi (`cfx_…`) + NULL grubu
 * kapsayıcı indeksi (`cfn_…`); yalnız tipli projeksiyon kullanan çoklu alanlar → fiziksel ikincil indeks yok.
 */
export function customFieldPhysicalComponents(def) {
    return customFieldSqlExpr(def).kind === null ? [] : ['sort', 'null_group'];
}
export function customFieldSqlExpr(def, column = 'custom_values') {
    if (!CUSTOM_FIELD_KEY_RE.test(def.key))
        throw new Error('Geçersiz alan anahtarı');
    const kind = def.type === 'formula'
        ? formulaSqlKind(def.options.resultType)
        : CUSTOM_FIELD_SQL_KIND[def.type];
    const text = `(${column}->>'${def.key}')`;
    /**
     * K16 kapanışı (migration 0023): kullanıcı verisine SERT CAST YOK. `cf_parse_*` geçersiz değerde hata yerine NULL
     * döner (= alan boş), böylece bozuk tek JSONB değeri o alanı sorgulayan tenant sorgusunu düşürmez. Tipli projeksiyon
     * (`cf_projection_rows`) AYNI fonksiyonları kullanır → kanonik ile projeksiyonun kabul kuralı yapısal olarak aynıdır.
     * Fonksiyonlar IMMUTABLE'dır (ifade indeksi kurulabilir); LEAKPROOF DEĞİLDİR (sahip kuralı).
     */
    if (def.type === 'currency')
        return { expr: `cf_parse_numeric((${column}->'${def.key}'->>'amount'))`, kind: 'numeric' };
    switch (kind) {
        case 'numeric':
            return { expr: `cf_parse_numeric(${text})`, kind };
        case 'date':
            return { expr: `cf_parse_date(${text})`, kind };
        case 'timestamp':
            return { expr: `cf_parse_timestamptz(${text})`, kind };
        case 'boolean':
            return { expr: `cf_parse_boolean(${text})`, kind };
        case 'text':
            return { expr: text, kind };
        default:
            return { expr: text, kind: null };
    }
}
export function formulaSqlKind(t) {
    return t === 'number' ? 'numeric' : t === 'date' ? 'date' : t === 'boolean' ? 'boolean' : 'text';
}
export function customFieldProjectionKind(def) {
    if (def.type === 'multiselect' || def.type === 'record_multi')
        return 'text_multi';
    const kind = def.type === 'formula'
        ? formulaSqlKind(def.options.resultType)
        : CUSTOM_FIELD_SQL_KIND[def.type];
    switch (kind) {
        case 'numeric':
            return 'numeric';
        case 'date':
            return 'date';
        case 'timestamp':
            return 'timestamptz';
        case 'boolean':
            return 'boolean';
        case 'text':
            return 'text';
        default:
            return null;
    }
}
/**
 * `cf_numeric_key` kodlayıcı sürümü (SQL ikizi: `cf_numeric_key_version()`, migration 0019). Kodlayıcı değişirse artırılır;
 * eski sürümle üretilmiş projeksiyon derleyicide kullanılmaz (kanonik yola düşer) ve yeniden indeks isteğiyle baştan üretilir.
 */
export const CF_NUMERIC_KEY_VERSION = 1;
/** Metin projeksiyonunda tam değer/önek anahtarı uzunluk sınırı (migration 0017 ile aynı). */
export const CUSTOM_FIELD_PROJECTION_TEXT_MAX = 200;
export function isIndexableType(def) {
    return customFieldSqlExpr({ key: 'kk', type: def.type, options: def.options }).kind !== null;
}
//# sourceMappingURL=custom-fields.js.map